import moment from "moment";

// Helpers for the clinic appointment-source report (รายงานช่องทางนัดหมาย).

export const UNSPECIFIED_CHANNEL = "ไม่ระบุ";
export const MISSING_CREATOR_NAME = "ไม่พบผู้ใช้";
export const MULTI_CHANNEL_NOTE = "คนไข้ที่ระบุหลายช่องทางนับในทุกช่องทาง";

// Bangkok is UTC+7 all year (no DST).
const BANGKOK_OFFSET_MINUTES = 420;

// Pivot the API's flat `trend` rows (period x channel) into a table.
// mode 'new' counts newPatient only; 'all' counts newPatient + returning.
// Periods run oldest to newest; channels by total desc with "ไม่ระบุ" always last.
// The รวม column (totals.byPeriod) and totals.grand come from the API's `trendTotals`,
// which counts each appointment once: summing the channel cells would double-count
// appointments with several channels. A period missing from trendTotals totals 0.
export function toTrendTable(trend, { mode = "new", trendTotals } = {}) {
  const valueOf = row =>
    mode === "all"
      ? (row.newPatient || 0) + (row.returning || 0)
      : row.newPatient || 0;

  const rows = trend || [];
  const totalRows = trendTotals || [];
  const periods = [
    ...new Set([...rows.map(r => r.period), ...totalRows.map(r => r.period)]),
  ].sort();
  const byChannel = {};
  const amounts = {};
  for (const row of rows) {
    const v = valueOf(row);
    if (!amounts[row.period]) amounts[row.period] = {};
    amounts[row.period][row.channel] =
      (amounts[row.period][row.channel] || 0) + v;
    byChannel[row.channel] = (byChannel[row.channel] || 0) + v;
  }

  const byPeriod = {};
  for (const period of periods) byPeriod[period] = 0;
  for (const row of totalRows) byPeriod[row.period] += valueOf(row);

  const channels = Object.keys(byChannel).sort((a, b) => {
    if (a === UNSPECIFIED_CHANNEL) return 1;
    if (b === UNSPECIFIED_CHANNEL) return -1;
    return byChannel[b] - byChannel[a] || a.localeCompare(b, "th");
  });

  const cells = {};
  for (const period of periods) {
    cells[period] = {};
    for (const channel of channels) {
      cells[period][channel] = (amounts[period] && amounts[period][channel]) || 0;
    }
  }

  const grand = Object.values(byPeriod).reduce((s, v) => s + v, 0);
  return { periods, channels, cells, totals: { byPeriod, byChannel, grand } };
}

// Label for a trend period (YYYY-MM-DD of the bucket's first day).
export function periodLabel(period, groupBy) {
  const m = moment(period, "YYYY-MM-DD").locale("th");
  if (groupBy === "month") return m.format("MMM YYYY");
  if (groupBy === "week") return `สัปดาห์ ${m.format("D MMM YY")}`;
  return m.format("D MMM YY");
}

export const ROW_SHEET_HEADERS = [
  "HN",
  "ชื่อ",
  "วันที่นัด",
  "วันที่สร้าง",
  "สาขา",
  "ผู้สร้าง",
  "สถานะ",
  "นัดเพื่อ",
  "ช่องทาง",
  "หมายเหตุนัด",
  "ใหม่/เก่า",
];

// Appointment/creation time in Asia/Bangkok whatever the browser's timezone is
// (the API sends UTC ISO strings).
export const dateTime = v =>
  v
    ? moment(v)
        .utcOffset(BANGKOK_OFFSET_MINUTES)
        .locale("th")
        .format("D MMM YY HH:mm")
    : "-";

// One array of cell strings per appointment row, in ROW_SHEET_HEADERS order.
// Every row is kept and the note is the full text (the on-screen list is paginated).
export function toRowsSheet(rows) {
  return (rows || []).map(r => [
    r.hn || "-",
    r.patientName || "-",
    dateTime(r.startDt),
    dateTime(r.creationDt),
    r.branchName || "",
    r.creatorName || MISSING_CREATOR_NAME,
    r.statusName || "",
    (r.apptTreatNames || []).join(", "),
    (r.channels || []).join(", "),
    r.note || "",
    r.isNewPatient ? "ใหม่" : "เก่า",
  ]);
}

// Every row of the report, fetched one page at a time (sequentially, to keep the load
// on the API down). fetchPage(page, pageSize) resolves to the API response
// ({ rows, pagination: { total } }). Stops once `total` rows are in, or on an empty page.
// Any failed page rejects the whole call, so a caller never sees a partial list.
export async function collectAllRows(fetchPage, pageSize) {
  const all = [];
  for (let page = 1; ; page++) {
    const res = await fetchPage(page, pageSize);
    const rows = (res && res.rows) || [];
    const total = (res && res.pagination && res.pagination.total) || 0;
    all.push(...rows);
    if (rows.length === 0 || all.length >= total) return all;
  }
}

// Session map uid -> name of every creator seen so far (returns a new object).
export function rememberCreators(seen, creators) {
  const next = { ...(seen || {}) };
  for (const c of creators || []) next[c.uid] = c.name;
  return next;
}

// Creator dropdown options: the response's creators, plus any selected uid the response
// no longer has (e.g. after a range change), so a selection that still filters the report
// is always visible and can be removed.
export function mergeCreatorOptions(creators, selectedUids, seenNames) {
  const options = [...(creators || [])];
  const present = new Set(options.map(c => c.uid));
  for (const uid of selectedUids || []) {
    if (present.has(uid)) continue;
    present.add(uid);
    const name = seenNames && seenNames[uid];
    options.push({ uid, name: name || MISSING_CREATOR_NAME });
  }
  return options;
}

const DATE_FIELD_TEXT = {
  start_dt: "นับตามวันที่นัด",
  creation_dt: "นับตามวันที่สร้างนัด",
};
const GROUP_BY_TEXT = { day: "รายวัน", week: "รายสัปดาห์", month: "รายเดือน" };

// Filters of a fetched report, for the Excel header. branchText goes into the export's
// "สาขา:" line; line states the count basis, creators and grouping.
export function describeFilters(
  { dateField, groupBy, branchIds, creatorUids } = {},
  { branches, creatorNames } = {},
) {
  const scope = branches || [];
  const ids = branchIds || [];
  const uids = creatorUids || [];
  // Scope order; an id outside the scope (the API drops those) is shown as the id.
  const branchNames = [
    ...scope.filter(b => ids.includes(b.id)).map(b => b.name || b.code || String(b.id)),
    ...ids.filter(id => !scope.some(b => b.id === id)).map(String),
  ];
  const branchText = ids.length ? branchNames.join(", ") : "ทุกสาขา";
  const creatorText = uids.length
    ? uids.map(uid => (creatorNames && creatorNames[uid]) || MISSING_CREATOR_NAME).join(", ")
    : "ทุกคน";
  const line = [
    DATE_FIELD_TEXT[dateField] || DATE_FIELD_TEXT.start_dt,
    `ผู้สร้างนัด: ${creatorText}`,
    `แสดงเป็น: ${GROUP_BY_TEXT[groupBy] || GROUP_BY_TEXT.day}`,
  ].join(" · ");
  return { branchText, line };
}
